import { useState } from 'react';
import { Link } from 'react-router';
import { CheckCircle2, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProgressReportForm, type SubmitOutcome } from '@/components/progress/ProgressReportForm';
import { useCurrentProgressReport } from '@/services/progress-reports';
import { useAuthStore } from '@/store/useAuthStore';

/**
 * The weekly progress report page.
 *
 * The form owns submission; this page owns what the client sees afterwards, and
 * fetches this week's report before the form mounts. Keeping the outcome here
 * means the sent and updated states are plain rendering rather than form state,
 * and can be tested without a form.
 */
export default function ProgressReport() {
    const user = useAuthStore((state) => state.user);
    const [outcome, setOutcome] = useState<SubmitOutcome | null>(null);
    // The form reads its starting values once, on mount, so it must not be
    // rendered until this has settled — otherwise a client who already sent this
    // week's report gets a blank form for a moment and may start typing into it.
    const current = useCurrentProgressReport();

    const clientId = user?.client?.id;

    return (
        <div className="min-h-screen bg-brand-page py-6">
            <div className="mx-auto max-w-2xl px-4">
                <header className="mb-6">
                    <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
                        <ClipboardList
                            className="h-6 w-6 text-brand-text-green"
                            aria-hidden="true"
                        />
                        Weekly progress report
                    </h1>
                    <p className="mt-1 text-gray-600">
                        This goes to your coach before your next session. It takes about five
                        minutes, and your answers are saved as you go.
                    </p>
                </header>

                <div className="rounded-2xl border border-brand-surface-edge bg-brand-surface p-5 shadow-sm sm:p-6">
                    {outcome ? (
                        <Outcome outcome={outcome} />
                    ) : !clientId ? (
                        <p role="alert" className="text-gray-700">
                            We could not load your profile, so this form is not available right now.
                            Please refresh the page, or contact your coach if it keeps happening.
                        </p>
                    ) : current.isPending ? (
                        <p className="py-6 text-center text-gray-700">Loading your report…</p>
                    ) : current.isError ? (
                        // Deliberately not "start a blank form anyway". Submitting
                        // into a week whose stored answers could not be read would
                        // overwrite them with whatever this form happened to hold.
                        <p role="alert" className="text-gray-700">
                            We could not check whether you have already sent this week’s report.
                            Please refresh the page, or contact your coach if it keeps happening.
                        </p>
                    ) : (
                        <ProgressReportForm
                            clientId={clientId}
                            existing={current.data.report}
                            onFinished={setOutcome}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

/** Headline and body per outcome. All three are a success; none is a dead end. */
const OUTCOME_COPY: Record<SubmitOutcome['status'], { title: string; body: string }> = {
    created: {
        title: 'Thank you',
        body: 'Your progress report has been sent to your coach. You will talk it through at your next session.',
    },
    updated: {
        title: 'Your report has been updated',
        body: 'Your coach will see your corrected answers for this week, not a second report. You can come back and change it again if you need to.',
    },
    'concurrent-submit': {
        title: 'This report is already in',
        body: 'It looks like this report went through a moment ago, so we have not sent it twice. Your coach has your answers.',
    },
};

function Outcome({ outcome }: { outcome: SubmitOutcome }) {
    const copy = OUTCOME_COPY[outcome.status];

    return (
        <div className="py-6 text-center">
            <CheckCircle2
                className="mx-auto h-12 w-12 text-brand-text-green"
                aria-hidden="true"
                data-testid="submit-success-icon"
            />
            <h2 className="mt-4 text-xl font-bold text-gray-900" tabIndex={-1}>
                {copy.title}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-gray-700">{copy.body}</p>
            <div className="mt-6">
                <Link to="/dashboard">
                    <Button className="min-h-11 px-6">Back to my dashboard</Button>
                </Link>
            </div>
        </div>
    );
}
